/**
 * BLE client for thermal printer. Web Bluetooth only (Chrome/Edge on Android).
 * Connect/reconnect must be triggered by user gesture (button click).
 */

import * as web from './bleClientWeb';
import type { ConnectOptions } from './bleClientWeb';
import type { PhomemoConnectionState, ScannedDevice } from './types';

export function isPhomemoAvailable(): boolean {
  return web.isPhomemoAvailable();
}

export function isSecureContext(): boolean {
  return web.isSecureContext();
}

/** Not used for Web Bluetooth (no scan list). Kept for API compatibility. */
export async function scan(): Promise<ScannedDevice[]> {
  return [];
}

export type { ConnectOptions, PrinterMode } from './bleClientWeb';

/**
 * Connect (user gesture required). Opens browser device chooser.
 * Returns { deviceId, deviceName } on success.
 */
export async function connect(options?: ConnectOptions): Promise<{ deviceId: string; deviceName?: string }> {
  return web.connect(options);
}

/**
 * Reconnect using saved device id (user gesture required).
 * Uses getDevices() when available; else requestDevice().
 */
export async function reconnect(savedDeviceId: string, options?: ConnectOptions): Promise<{ deviceId: string; deviceName?: string }> {
  return web.reconnect(savedDeviceId, options);
}

/**
 * Silent reconnect using only cached devices (no user gesture). Returns true if reconnected.
 */
export async function tryReconnectFromCache(savedDeviceId: string, options?: ConnectOptions): Promise<boolean> {
  return web.tryReconnectFromCache(savedDeviceId, options);
}

export async function disconnect(): Promise<void> {
  return web.disconnect();
}

export async function getConnectionState(): Promise<{ state: PhomemoConnectionState; deviceName?: string }> {
  return web.getConnectionState();
}

export async function writeChunk(data: Uint8Array): Promise<void> {
  return web.writeChunk(data);
}

/**
 * Write large data (e.g. shipping label bitmap) in chunks with delays.
 * BLE requires chunking for reliable transmission of tens of KB.
 */
export async function writeAll(data: Uint8Array, chunkSize?: number, delayMs?: number): Promise<void> {
  return web.writeAll(data, chunkSize, delayMs);
}

export function onConnectionChange(callback: (state: PhomemoConnectionState, deviceName?: string) => void): () => void {
  return web.onConnectionChange(callback);
}
